import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { getApiWsRoot } from '@/helpers/api';
import { getKeycloak } from '@/helpers/keycloak';
import { useI18n } from 'vue-i18n';
import { useMonitoringGeneralStore } from '@/store/monitoringGeneral';
import { getSubdomain } from '@/helpers/util';

const retryLimit = 5;
const retryInterval = 5 * 1000;

const firstMessageTimeout = 15000;

export const useMonitoringRealTimeStore = defineStore('monitoringRealTimeStore', () => {
  const { t } = useI18n();

  const { currentDeviceId, currentDeviceType } = storeToRefs(useMonitoringGeneralStore());

  const loadingData = ref(false);
  const data = ref({});
  const socket = ref();
  const socketStatus = ref();

  const lastViewed = ref();

  const closedByServer = ref(false);
  const shouldRetry = ref(true);
  const retries = ref(0);

  const socketOpen = computed(() => socketStatus.value === WebSocket.OPEN);
  const socketClosed = computed(() => socketStatus.value === WebSocket.CLOSED);

  const currentSocketDeviceId = computed(() => {
    const params = new URLSearchParams(socket.value?.url.split('?')[1]);
    return params.get('deviceId');
  });

  const errorMessage = computed(() => closedByServer.value ? t('monitoring.connectionLost') : t('monitoring.noConnection'));

  async function initiateBroadcast() {
    resetBroadcast();
    loadingData.value = true;
    lastViewed.value = Date.now();

    return connectBroadcast();
  }

  function resetBroadcast() {
    stopBroadcast();
    loadingData.value = false;
    closedByServer.value = false;
    shouldRetry.value = true;
    retries.value = 0;
  }

  async function connectBroadcast() {
    socket.value = new WebSocket(getApiWsRoot() + '/monitoring?subdomain=' + getSubdomain() + '&accessToken=' + (await getKeycloak()).token + '&type=' + currentDeviceType.value + '&deviceId=' + currentDeviceId.value);

    socket.value.addEventListener('error', (event) => {
      console.error(event);
      socketStatus.value = socket.value?.readyState;
    });

    socket.value.addEventListener('open', () => {
      socketStatus.value = socket.value?.readyState;
      setFirstMessageTimeout();
    });

    socket.value.addEventListener('message', (event) => {
      loadingData.value = false;

      if (event.data instanceof Blob) {
        const reader = new FileReader();

        reader.onload = () => {
          data.value = JSON.parse(reader.result);
        };

        reader.readAsText(event.data);

        return;
      }

      data.value = JSON.parse(event.data);
    });

    socket.value.addEventListener('close', (event) => {
      socketStatus.value = socket.value?.readyState;

      if (!shouldRetry.value || event.code === 1000) {
        return;
      }

      if (retries.value < retryLimit) {
        retries.value++;
        setTimeout(connectBroadcast, retryInterval);

        return;
      }

      closedByServer.value = true;
    });
  }

  function stopBroadcast() {
    unsubscribeBroadcast();
    socket.value = null;
    data.value = {};
  }

  function unsubscribeBroadcast() {
    shouldRetry.value = false;
    socket.value?.close(1000);
  }

  function setFirstMessageTimeout() {
    setTimeout(() => {
      if (loadingData.value) {
        unsubscribeBroadcast();
        loadingData.value = false;
      }
    }, firstMessageTimeout);
  }

  return {
    data,
    socket,
    socketOpen,
    socketClosed,
    currentSocketDeviceId,
    lastViewed,
    loadingData,
    closedByServer,
    errorMessage,
    initiateBroadcast,
    unsubscribeBroadcast,
    resetBroadcast,
  };
});
